import {
  INTL_DAY_MONTH_NUMERIC,
  INTL_SHORT_TIME_FORMAT_AM_PM,
  ISO_DATE_FORMAT,
} from 'constants/dateFormats'
import {
  MAPBOX_STATIC_IMAGE_BASE_URL,
  MAPBOX_STATIC_IMAGE_MAX_DIMENSION,
  MAPBOX_STATIC_IMAGE_MIN_DIMENSION,
  PROPERTY_MAP_COORDINATE_PRECISION,
  PROPERTY_MAP_SINGLE_MARKER_ZOOM,
} from 'components/Common/PropertyMap/constants'
import type GlobalSearchState from 'contexts/GlobalSearch/GlobalSearchState'
import { intlDateFormatter } from 'lib/datetime/dateUtils'
import { clamp } from 'lib/maths/mathUtils'
import moment from 'moment'
import { createElement, type ReactNode } from 'react'
import type { IntlShape } from 'react-intl'
import SolidPlaneDepartureIcon from 'LuxKit/Icons/solid/SolidPlaneDepartureIcon'
import SolidBedIcon from 'LuxKit/Icons/solid/SolidBedIcon'

const ADDRESS_SEARCH_TYPES: Array<App.SearchType> = ['address']
const AIRPORT_AND_ADDRESS_SEARCH_TYPES: Array<App.SearchType> = ['airport', 'address']
const AIRPORT_SEARCH_TYPES: Array<App.SearchType> = ['airport']
const MAPBOX_TILE_SIZE = 512
const MAPBOX_MAX_LATITUDE = 85.0511
const MAPBOX_MAX_ZOOM = 22
const ISO_TIMESTAMP_WITH_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/

export interface AirportTransferRouteMapCoordinates {
  latitude: number
  longitude: number
}

export interface AirportTransferRouteMapPoint extends AirportTransferRouteMapCoordinates {
  icon?: ReactNode
  label?: string
}

export interface AirportTransferRouteMapSize {
  height: number
  width: number
}

export interface AirportTransferRouteMapViewport {
  centerLatitude: number
  centerLongitude: number
  zoom: number
}

type AirportTransferItem = Pick<App.OrderTransferItem, 'type'>

function getSearchItem(
  name: string | null,
  id: string | null,
  searchType: App.SearchType | undefined,
): App.SearchItem | undefined {
  if (!name) return undefined

  if (searchType === 'airport') {
    return {
      searchType,
      value: id ?? name,
      airportCode: id ?? '',
      format: { mainText: name, secondaryText: '' },
    }
  }

  return {
    searchType: searchType ?? 'property',
    value: id ?? name,
    format: { mainText: name, secondaryText: '' },
  } as App.SearchItem
}

function getSearchType(value: string | null): App.SearchType | undefined {
  if (value === 'address' || value === 'airport' || value === 'property') return value

  return undefined
}

function getJourneyType(
  value: string | null,
): GlobalSearchState['airportTransferJourneyType'] | undefined {
  if (value === 'one-way' || value === 'return') return value

  return undefined
}

function getDate(value: string | null): moment.Moment | undefined {
  if (!value) return undefined

  const date = moment(value, ISO_DATE_FORMAT, true)

  return date.isValid() ? date : undefined
}

function getTime(value: string | null): string | undefined {
  if (!value || !moment(value, 'HH:mm', true).isValid()) return undefined

  return value
}

function getCount(value: string | null, minimum: number): number | undefined {
  if (!value || !/^\d+$/.test(value)) return undefined

  const count = Number(value)

  if (!Number.isSafeInteger(count) || count < minimum) return undefined

  return count
}

export function isAirportTransferReturnBeforePickup(
  pickUpDate: string | undefined,
  pickUpTime: string | undefined,
  returnDate: string | undefined,
  returnTime: string | undefined,
): boolean {
  if (!pickUpDate || !pickUpTime || !returnDate || !returnTime) return false

  const pickUpDateTime = moment(
    `${pickUpDate} ${pickUpTime}`,
    `${ISO_DATE_FORMAT} HH:mm`,
    true,
  )
  const returnDateTime = moment(
    `${returnDate} ${returnTime}`,
    `${ISO_DATE_FORMAT} HH:mm`,
    true,
  )

  return returnDateTime.isBefore(pickUpDateTime)
}

export function isAirportTransferFreeCancellationExpired(
  freeCancellationUntil: string | undefined,
  now: number = Date.now(),
): boolean {
  if (!freeCancellationUntil || !ISO_TIMESTAMP_WITH_OFFSET.test(freeCancellationUntil)) {
    return false
  }

  const deadline = Date.parse(freeCancellationUntil)

  return Number.isFinite(deadline) && deadline <= now
}

export function getAirportTransferSearchStateFromParams(
  queryParams: URLSearchParams,
): Partial<GlobalSearchState> {
  const pickUpDate = getDate(queryParams.get('pickUpDate'))
  const pickUpTime = getTime(queryParams.get('pickUpTime'))
  let returnDate = getDate(queryParams.get('returnDate'))
  let returnTime = getTime(queryParams.get('returnTime'))
  let journeyType = getJourneyType(queryParams.get('journeyType'))

  if (isAirportTransferReturnBeforePickup(
    pickUpDate?.format(ISO_DATE_FORMAT),
    pickUpTime,
    returnDate?.format(ISO_DATE_FORMAT),
    returnTime,
  )) {
    returnDate = undefined
    returnTime = undefined

    if (journeyType === 'return') {
      journeyType = 'one-way'
    }
  }

  return {
    airportTransferJourneyType: journeyType,
    airportTransferLuggage: getCount(queryParams.get('luggage'), 0) ?? 0,
    airportTransferPassengers: getCount(queryParams.get('passengers'), 1) ?? 1,
    checkinDate: pickUpDate,
    checkoutDate: returnDate,
    dropOffTime: returnTime ?? '',
    pickUpTime: pickUpTime ?? '',
    searchItem: getSearchItem(
      queryParams.get('pickUpName'),
      queryParams.get('pickUpId'),
      getSearchType(queryParams.get('pickUpSearchType')),
    ),
    secondarySearchItem: getSearchItem(
      queryParams.get('dropOffName'),
      queryParams.get('dropOffId'),
      getSearchType(queryParams.get('dropOffSearchType')),
    ),
  }
}

export function getDateTimeLabel(date: moment.Moment | undefined, time: string | undefined, intl: IntlShape) {
  if (!date || !time) {
    return ''
  }

  const formattedDate = intlDateFormatter(moment(date), INTL_DAY_MONTH_NUMERIC, intl.locale)
  const localizedTime = intlDateFormatter(moment(time, 'HH:mm'), INTL_SHORT_TIME_FORMAT_AM_PM, intl.locale)
  return `${formattedDate} · ${localizedTime}`
}

export function orderAirportTransferSuggestions(items: Array<App.SearchItem>, searchPhrase: string): Array<App.SearchItem> {
  const airportCode = searchPhrase.trim().toUpperCase()
  if (!/^[A-Z]{3}$/.test(airportCode)) return items

  const matchIndex = items.findIndex(item => item.searchType === 'airport' && item.airportCode.toUpperCase() === airportCode)
  if (matchIndex <= 0) return items

  return [items[matchIndex], ...items.slice(0, matchIndex), ...items.slice(matchIndex + 1)]
}

export function airportToSearchItem(airport: App.AirportLocation): App.SearchAirport {
  return {
    searchType: 'airport',
    value: airport.airportCode,
    airportCode: airport.airportCode,
    format: {
      mainText: airport.airportName,
      secondaryText: airport.country,
    },
  }
}

export function getAirportTransferLocationName(searchItem?: App.SearchItem): string | undefined {
  if (!searchItem) return undefined

  if (searchItem.searchType === 'airport') {
    const airportCode = getAirportTransferLocationId(searchItem)
    const airportName = airportCode
      ? searchItem.format.mainText.replace(/\s*\([A-Za-z]{3}(?:-[^)]+)?\)\s*$/, '')
      : searchItem.format.mainText

    return [airportName, airportCode && `(${airportCode})`].filter(Boolean).join(' ')
  }

  return [
    searchItem.format.mainText,
    searchItem.format.secondaryText,
  ].filter(Boolean).join(', ')
}

export function getAirportTransferLocationId(searchItem?: App.SearchItem): string | undefined {
  if (!searchItem) return undefined

  if (searchItem.searchType === 'airport') {
    return /^[A-Za-z]{3}$/.test(searchItem.airportCode)
      ? searchItem.airportCode.toUpperCase()
      : undefined
  }

  if (
    searchItem.searchType === 'address' &&
    /^[A-Za-z0-9_-]+$/.test(searchItem.value)
  ) {
    return searchItem.value
  }

  return undefined
}

export function getAirportTransferLocationSearchTypes(alternateSearchItem?: App.SearchItem): Array<App.SearchType> {
  if (alternateSearchItem?.searchType === 'airport') {
    return ADDRESS_SEARCH_TYPES
  }

  if (alternateSearchItem?.searchType === 'address' || alternateSearchItem?.searchType === 'property') {
    return AIRPORT_SEARCH_TYPES
  }

  return AIRPORT_AND_ADDRESS_SEARCH_TYPES
}

export function orderAirportTransferItems<Item extends AirportTransferItem>(items: Array<Item>): Array<Item> {
  const pickupItems = items.filter(item => item.type === 'AIRPORT-TO-HOTEL')
  const returnItems = items.filter(item => item.type === 'HOTEL-TO-AIRPORT')

  return [...pickupItems, ...returnItems]
}

export function getAirportTransferWaitTimeMins(
  transfer: AirportTransferItem | undefined,
): number {
  if (transfer?.type === 'HOTEL-TO-AIRPORT') {
    return 15
  }

  return 45
}

function getAirportTransferSearchLocationParams(
  location: App.ExperienceTransferView['pickupPoint'],
) {
  let searchType: App.SearchType = 'property'
  let id: string | undefined

  if (location?.iataCode) {
    searchType = 'airport'
    id = location.iataCode
  }

  return {
    id,
    name: location?.name,
    searchType,
  }
}

export function getAirportTransferSearchParams(
  transfers: Array<App.ExperienceTransferView>,
) {
  const orderedTransfers = orderAirportTransferItems(transfers)
  const pickupTransfer = orderedTransfers[0]
  const returnTransfer = orderedTransfers.find(
    transfer => transfer.type === 'HOTEL-TO-AIRPORT' && transfer !== pickupTransfer,
  )
  const pickupLocation = getAirportTransferSearchLocationParams(
    pickupTransfer?.pickupPoint,
  )
  const dropoffLocation = getAirportTransferSearchLocationParams(
    pickupTransfer?.dropoffPoint,
  )
  const hasReturnJourney = Boolean(returnTransfer?.date && returnTransfer.time)

  return {
    pickUpName: pickupLocation.name,
    pickUpId: pickupLocation.id,
    pickUpSearchType: pickupLocation.searchType,
    dropOffName: dropoffLocation.name,
    dropOffId: dropoffLocation.id,
    dropOffSearchType: dropoffLocation.searchType,
    pickUpDate: pickupTransfer?.date,
    pickUpTime: pickupTransfer?.time,
    journeyType: hasReturnJourney ? 'return' : 'one-way',
    ...(hasReturnJourney && {
      returnDate: returnTransfer?.date,
      returnTime: returnTransfer?.time,
    }),
    passengers: (pickupTransfer?.travellers.adults ?? 2) +
      (pickupTransfer?.travellers.children ?? 0),
    luggage: pickupTransfer?.baggage.bags ?? 0,
  }
}

export function getAirportTransferRouteMapPoint(
  location?: Omit<App.ExperiencePickupPoint, 'id'>,
): AirportTransferRouteMapPoint | undefined {
  const label = location?.description ?? location?.name

  if (
    !label ||
    location?.latitude === undefined ||
    location.longitude === undefined
  ) {
    return undefined
  }

  const isAirport = !!location.iataCode

  return {
    label,
    latitude: location.latitude,
    longitude: location.longitude,
    icon: createElement(isAirport ? SolidPlaneDepartureIcon : SolidBedIcon, { size: 'XXS' }),
  }
}

function formatRouteMapCoordinate(value: number) {
  return Number(value.toFixed(PROPERTY_MAP_COORDINATE_PRECISION)).toString()
}

function projectRouteMapCoordinatesToWebMercator(
  latitude: number,
  longitude: number,
) {
  const safeLatitude = clamp(-MAPBOX_MAX_LATITUDE, latitude, MAPBOX_MAX_LATITUDE)
  const latitudeRadians = (safeLatitude * Math.PI) / 180
  const sinLatitude = Math.sin(latitudeRadians)

  return {
    x: (longitude + 180) / 360,
    y: 0.5 - Math.log((1 + sinLatitude) / (1 - sinLatitude)) / (4 * Math.PI),
  }
}

function unprojectRouteMapWebMercatorLatitude(y: number) {
  return (Math.atan(Math.sinh(Math.PI * (1 - 2 * y))) * 180) / Math.PI
}

export function getAirportTransferRouteMapViewport(
  coordinates: Array<AirportTransferRouteMapCoordinates>,
  size: AirportTransferRouteMapSize,
  padding: number,
): AirportTransferRouteMapViewport | undefined {
  const firstCoordinates = coordinates[0]
  if (!firstCoordinates) return undefined

  if (coordinates.length === 1) {
    return {
      centerLatitude: firstCoordinates.latitude,
      centerLongitude: firstCoordinates.longitude,
      zoom: PROPERTY_MAP_SINGLE_MARKER_ZOOM,
    }
  }

  const projectedCoordinates = coordinates.map(point =>
    projectRouteMapCoordinatesToWebMercator(point.latitude, point.longitude),
  )
  const firstProjectedCoordinates = projectedCoordinates[0]
  const bounds = projectedCoordinates.reduce(
    (acc, point) => ({
      minX: Math.min(acc.minX, point.x),
      maxX: Math.max(acc.maxX, point.x),
      minY: Math.min(acc.minY, point.y),
      maxY: Math.max(acc.maxY, point.y),
    }),
    {
      minX: firstProjectedCoordinates.x,
      maxX: firstProjectedCoordinates.x,
      minY: firstProjectedCoordinates.y,
      maxY: firstProjectedCoordinates.y,
    },
  )
  const availableWidth = Math.max(1, size.width - padding * 2)
  const availableHeight = Math.max(1, size.height - padding * 2)
  const longitudeZoom = bounds.maxX === bounds.minX ? MAPBOX_MAX_ZOOM : Math.log2(availableWidth / (MAPBOX_TILE_SIZE * (bounds.maxX - bounds.minX)))
  const latitudeZoom = bounds.maxY === bounds.minY ? MAPBOX_MAX_ZOOM : Math.log2(availableHeight / (MAPBOX_TILE_SIZE * (bounds.maxY - bounds.minY)))
  const zoom = Number(clamp(0, Math.min(longitudeZoom, latitudeZoom, PROPERTY_MAP_SINGLE_MARKER_ZOOM), MAPBOX_MAX_ZOOM).toFixed(2))

  return {
    centerLatitude: unprojectRouteMapWebMercatorLatitude(
      (bounds.minY + bounds.maxY) / 2,
    ),
    centerLongitude: ((bounds.minX + bounds.maxX) / 2) * 360 - 180,
    zoom,
  }
}

export function projectAirportTransferRouteMapCoordinates(
  latitude: number,
  longitude: number,
  viewport: AirportTransferRouteMapViewport,
  size: AirportTransferRouteMapSize,
) {
  const point = projectRouteMapCoordinatesToWebMercator(latitude, longitude)
  const center = projectRouteMapCoordinatesToWebMercator(
    viewport.centerLatitude,
    viewport.centerLongitude,
  )
  const worldSize = MAPBOX_TILE_SIZE * 2 ** viewport.zoom

  return {
    x: size.width / 2 + (point.x - center.x) * worldSize,
    y: size.height / 2 + (point.y - center.y) * worldSize,
  }
}

export function buildAirportTransferRouteStaticMapUrl(
  accessToken: string,
  coordinates: Array<AirportTransferRouteMapCoordinates>,
  size: AirportTransferRouteMapSize,
  padding: number,
) {
  const viewport = getAirportTransferRouteMapViewport(coordinates, size, padding)
  if (!viewport) return undefined

  const width = clamp(
    MAPBOX_STATIC_IMAGE_MIN_DIMENSION,
    Math.round(size.width),
    MAPBOX_STATIC_IMAGE_MAX_DIMENSION,
  )
  const height = clamp(
    MAPBOX_STATIC_IMAGE_MIN_DIMENSION,
    Math.round(size.height),
    MAPBOX_STATIC_IMAGE_MAX_DIMENSION,
  )
  const formattedViewport = `${formatRouteMapCoordinate(viewport.centerLongitude)},${formatRouteMapCoordinate(viewport.centerLatitude)},${viewport.zoom}`
  const searchParams = new URLSearchParams({ access_token: accessToken })

  return `${MAPBOX_STATIC_IMAGE_BASE_URL}/${formattedViewport}/${width}x${height}?${searchParams.toString()}`
}
